import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
// eslint-disable-next-line
import { 
  User, 
  Mail, 
  Phone, 
  Globe, 
  Linkedin, 
  Instagram, 
  MessageCircle, 
  Youtube,
  Camera,
  ExternalLink,
  Save,
  Facebook,
  Twitter,
  Github
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfilePage() {
  const { t } = useTranslation();
  const { user, token, updateUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    bio: '',
    phone: '',
    office_phone: '',
    email: '',
    website: '',
    linkedin: '',
    instagram: '',
    whatsapp: '',
    youtube_channel: '',
    youtube_video: '',
    facebook: '',
    discord: '',
    twitter: '',
    tiktok: '',
    github: '',
    is_public: true
  });
  const [userInfo, setUserInfo] = useState({
    first_name: '',
    last_name: '',
    job_title: '',
    company: ''
  });
  const [photo, setPhoto] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${API}/profiles/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        
      });

      if (response.ok) {
        const data = await response.json();
        setProfile({
          bio: data.bio || '',
          phone: data.phone || '',
          office_phone: data.office_phone || '',
          email: data.email || '',
          website: data.website || '',
          linkedin: data.linkedin || '',
          instagram: data.instagram || '',
          whatsapp: data.whatsapp || '',
          youtube_channel: data.youtube_channel || '',
          youtube_video: data.youtube_video || '',
          facebook: data.facebook || '',
          discord: data.discord || '',
          twitter: data.twitter || '',
          tiktok: data.tiktok || '',
          github: data.github || '',
          is_public: data.is_public ?? true
        });
        setPhoto(data.photo);
      }

      // Set user info
      setUserInfo({
        first_name: user?.first_name || '',
        last_name: user?.last_name || '',
        job_title: user?.job_title || '',
        company: user?.company || ''
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleUserChange = (field, value) => {
    setUserInfo(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API}/profiles/me/photo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setPhoto(data.photo);
        toast.success('Photo uploaded!');
      }
    } catch (error) {
      toast.error('Failed to upload photo');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/profiles/me`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        
        body: JSON.stringify({
          ...profile,
          ...userInfo
        })
      });

      if (response.ok) {
        toast.success(t('common.success'));
        updateUser(userInfo);
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const profileUrl = `${window.location.origin}/user/${user?.profile_id}`;

  return (
    <div className="p-6 lg:p-8" data-testid="profile-page">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8"
      >
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">
            {t('dashboard.profile')}
          </h1>
          <p className="text-slate-600">
            Your profile ID: <span className="font-mono font-medium text-[#0A2540]">{user?.profile_id}</span>
          </p>
        </div>
        <div className="flex gap-3">
          <a href={profileUrl} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="rounded-xl gap-2" data-testid="view-profile-btn">
              <ExternalLink className="w-4 h-4" />
              Preview
            </Button>
          </a>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="rounded-xl gap-2"
            data-testid="save-profile-btn"
          >
            <Save className="w-4 h-4" />
            {saving ? t('common.loading') : t('dashboard.saveChanges')}
          </Button>
        </div>
      </motion.div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Left: Photo & Basic Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="lg:col-span-1 space-y-6"
        >
          {/* Photo */}
          <div className="bg-white rounded-2xl p-6 shadow-soft">
            <h2 className="font-heading font-semibold text-slate-900 mb-4">Profile Photo</h2>
            <div className="flex flex-col items-center">
              <div className="relative">
                {photo || user?.picture ? (
                  <img 
                    src={photo?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${photo}` : (photo || user?.picture)} 
                    alt="" 
                    className="w-32 h-32 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-32 h-32 rounded-full bg-slate-200 flex items-center justify-center">
                    <User className="w-12 h-12 text-slate-400" />
                  </div>
                )}
                <label className="absolute bottom-0 right-0 w-10 h-10 bg-slate-900 rounded-full flex items-center justify-center cursor-pointer hover:bg-slate-800 transition-colors">
                  <Camera className="w-5 h-5 text-white" />
                  <input 
                    type="file" 
                    accept="image/*" 
                    className="hidden" 
                    onChange={handlePhotoUpload}
                    data-testid="photo-upload"
                  />
                </label>
              </div>
              <p className="text-sm text-slate-500 mt-4 text-center">
                Click the camera icon to upload
              </p>
            </div>
          </div>

          {/* Public Toggle */}
          <div className="bg-white rounded-2xl p-6 shadow-soft">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-slate-900">{t('profile.publicProfile')}</h3>
                <p className="text-sm text-slate-500">Make your profile visible to everyone</p>
              </div>
              <Switch 
                checked={profile.is_public}
                onCheckedChange={(checked) => handleProfileChange('is_public', checked)}
                data-testid="public-toggle"
              />
            </div>
          </div>
        </motion.div>

        {/* Right: Form Fields */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="lg:col-span-2 space-y-6"
        >
          {/* Personal Info */}
          <div className="bg-white rounded-2xl p-6 shadow-soft">
            <h2 className="font-heading font-semibold text-slate-900 mb-4">Personal Information</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>{t('auth.firstName')}</Label>
                <Input
                  value={userInfo.first_name}
                  onChange={(e) => handleUserChange('first_name', e.target.value)}
                  className="rounded-xl mt-1"
                  data-testid="first-name-input"
                />
              </div>
              <div>
                <Label>{t('auth.lastName')}</Label>
                <Input
                  value={userInfo.last_name}
                  onChange={(e) => handleUserChange('last_name', e.target.value)}
                  className="rounded-xl mt-1"
                  data-testid="last-name-input"
                />
              </div>
              <div>
                <Label>Job Title</Label>
                <Input
                  value={userInfo.job_title}
                  onChange={(e) => handleUserChange('job_title', e.target.value)}
                  className="rounded-xl mt-1"
                  placeholder="e.g. Product Designer"
                  data-testid="job-title-input"
                />
              </div>
              <div>
                <Label>Company</Label>
                <Input
                  value={userInfo.company}
                  onChange={(e) => handleUserChange('company', e.target.value)}
                  className="rounded-xl mt-1"
                  placeholder="e.g. Acme Inc."
                  data-testid="company-input"
                />
              </div>
            </div>
            <div className="mt-4">
              <Label>{t('profile.bio')}</Label>
              <Textarea
                value={profile.bio}
                onChange={(e) => handleProfileChange('bio', e.target.value)}
                className="rounded-xl mt-1"
                rows={3}
                placeholder="Tell people about yourself..."
                data-testid="bio-input"
              />
            </div>
          </div>

          {/* Contact Info */}
          <div className="bg-white rounded-2xl p-6 shadow-soft">
            <h2 className="font-heading font-semibold text-slate-900 mb-4">Contact Information</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-2">
                  <Mail className="w-4 h-4" /> {t('profile.email')}
                </Label>
                <Input
                  type="email"
                  value={profile.email}
                  onChange={(e) => handleProfileChange('email', e.target.value)}
                  className="rounded-xl mt-1"
                  data-testid="email-input"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <Phone className="w-4 h-4" /> {t('profile.phone')}
                </Label>
                <Input
                  type="tel"
                  value={profile.phone}
                  onChange={(e) => handleProfileChange('phone', e.target.value)}
                  className="rounded-xl mt-1"
                  placeholder="+33 6 00 00 00 00"
                  data-testid="phone-input"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <Phone className="w-4 h-4" /> {t('profile.officePhone')}
                </Label>
                <Input
                  type="tel"
                  value={profile.office_phone}
                  onChange={(e) => handleProfileChange('office_phone', e.target.value)}
                  className="rounded-xl mt-1"
                  data-testid="office-phone-input"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <Globe className="w-4 h-4" /> {t('profile.website')}
                </Label>
                <Input
                  type="url"
                  value={profile.website}
                  onChange={(e) => handleProfileChange('website', e.target.value)}
                  className="rounded-xl mt-1"
                  placeholder="https://yourwebsite.com"
                  data-testid="website-input"
                />
              </div>
            </div>
          </div>

          {/* Social Links */}
          <div className="bg-white rounded-2xl p-6 shadow-soft">
            <h2 className="font-heading font-semibold text-slate-900 mb-4">Social Links</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-2">
                  <Linkedin className="w-4 h-4" /> {t('profile.linkedin')}
                </Label>
                <Input
                  type="url"
                  value={profile.linkedin}
                  onChange={(e) => handleProfileChange('linkedin', e.target.value)}
                  className="rounded-xl mt-1"
                  placeholder="https://linkedin.com/in/..."
                  data-testid="linkedin-input"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <Instagram className="w-4 h-4" /> {t('profile.instagram')}
                </Label>
                <Input
                  type="url"
                  value={profile.instagram}
                  onChange={(e) => handleProfileChange('instagram', e.target.value)}
                  className="rounded-xl mt-1"
                  placeholder="https://instagram.com/..."
                  data-testid="instagram-input"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <Facebook className="w-4 h-4" /> {t('profile.facebook')}
                </Label>
                <Input
                  type="url"
                  value={profile.facebook}
                  onChange={(e) => handleProfileChange('facebook', e.target.value)}
                  className="rounded-xl mt-1"
                  placeholder="https://facebook.com/..."
                  data-testid="facebook-input"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <Twitter className="w-4 h-4" /> {t('profile.twitter')}
                </Label>
                <Input
                  type="url"
                  value={profile.twitter}
                  onChange={(e) => handleProfileChange('twitter', e.target.value)}
                  className="rounded-xl mt-1"
                  placeholder="https://x.com/..."
                  data-testid="twitter-input"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4" /> {t('profile.whatsapp')}
                </Label>
                <Input
                  type="tel"
                  value={profile.whatsapp}
                  onChange={(e) => handleProfileChange('whatsapp', e.target.value)}
                  className="rounded-xl mt-1"
                  placeholder="+33 6 00 00 00 00"
                  data-testid="whatsapp-input"
                />
              </div>
              <div>
                <Label>Discord</Label>
                <Input
                  type="url"
                  value={profile.discord}
                  onChange={(e) => handleProfileChange('discord', e.target.value)}
                  className="rounded-xl mt-1"
                  placeholder="https://discord.gg/..."
                  data-testid="discord-input"
                />
              </div>
              <div>
                <Label>TikTok</Label>
                <Input
                  type="url"
                  value={profile.tiktok}
                  onChange={(e) => handleProfileChange('tiktok', e.target.value)}
                  className="rounded-xl mt-1"
                  placeholder="https://tiktok.com/@..."
                  data-testid="tiktok-input"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <Github className="w-4 h-4" /> {t('profile.github')}
                </Label>
                <Input
                  type="url"
                  value={profile.github}
                  onChange={(e) => handleProfileChange('github', e.target.value)}
                  className="rounded-xl mt-1"
                  placeholder="https://github.com/..."
                  data-testid="github-input"
                />
              </div>
            </div>
          </div>

          {/* YouTube */}
          <div className="bg-white rounded-2xl p-6 shadow-soft">
            <h2 className="font-heading font-semibold text-slate-900 mb-4">YouTube</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-2">
                  <Youtube className="w-4 h-4" /> {t('profile.youtubeChannel')}
                </Label>
                <Input
                  type="url"
                  value={profile.youtube_channel}
                  onChange={(e) => handleProfileChange('youtube_channel', e.target.value)}
                  className="rounded-xl mt-1"
                  placeholder="https://youtube.com/@..."
                  data-testid="youtube-channel-input"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <Youtube className="w-4 h-4" /> {t('profile.youtubeVideo')}
                </Label>
                <Input
                  type="url"
                  value={profile.youtube_video}
                  onChange={(e) => handleProfileChange('youtube_video', e.target.value)}
                  className="rounded-xl mt-1"
                  placeholder="https://youtube.com/watch?v=..."
                  data-testid="youtube-video-input"
                />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
